package dev.kotlinfest._2026.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.BrowserNotification
import dev.kotlinfest._2026.JapaneseFontFamily
import dev.kotlinfest._2026.LatinFontFamily
import dev.kotlinfest._2026.QrCode
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.layouts.PageLayoutData
import dev.kotlinfest._2026.desktopPageHorizontalPadding
import dev.kotlinfest._2026.encodeURIComponent
import dev.kotlinfest._2026.models.MyTimetable
import dev.kotlinfest._2026.models.UserSession
import dev.kotlinfest._2026.models.UserSessions
import dev.kotlinfest._2026.sessionVenueColor
import dev.kotlinfest._2026.sitePageHero
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

private val timetablePink = Color.rgb(0xF28FC1)
private val timetableBlue = Color.rgb(0x78ACD7)
private val timetableLime = Color.rgb(0xDDE68E)
private val _sessionLinkVariant = UndecoratedLinkVariant.then(UncoloredLinkVariant)

val TimetableHeroStyle = CssStyle {
    base {
        Modifier
            .sitePageHero(240)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .justifyContent(JustifyContent.Center)
            .alignItems(AlignItems.Center)
    }
    Breakpoint.MD { Modifier.sitePageHero(380) }
}

val TimetableHeroTitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(LatinFontFamily, "sans-serif")
            .fontSize(32.px)
            .fontWeight(FontWeight.SemiBold)
            .lineHeight(1.2)
            .letterSpacing(0.16.em)
            .color(SitePalette.brand.ink)
    }
    Breakpoint.MD { Modifier.fontSize(48.px) }
}

val TimetableHeroSubStyle = CssStyle.base {
    Modifier
        .fontFamily(JapaneseFontFamily, "sans-serif")
        .fontSize(12.px)
        .fontWeight(FontWeight.Bold)
        .letterSpacing(0.1.em)
        .color(SitePalette.brand.ink)
}

val TimetableContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(0.55.cssRem)
            .padding(top = 2.cssRem, bottom = 3.cssRem, leftRight = SiteLayout.MobileMarginPx.px)
            .styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD {
        Modifier
            .maxWidth(SiteLayout.DesktopDesignWidthPx.px)
            .padding(top = 4.cssRem, bottom = 4.cssRem)
            .desktopPageHorizontalPadding()
            .styleModifier {
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
    }
}

val VenueRowStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .padding(left = 4.4.cssRem)
            .gap(0.8.cssRem)
    }
}

val VenueLabelStyle = CssStyle.base {
    Modifier
        .height(2.15.cssRem)
        .borderRadius(0.35.cssRem)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .fontSize(1.05.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(SitePalette.brand.ink)
        .styleModifier { property("flex", "1 1 0") }
}

val MobileSlotStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(0.45.cssRem).padding(top = 0.35.cssRem, bottom = 0.35.cssRem)
    }
    Breakpoint.MD { Modifier.display(DisplayStyle.None) }
}

val DesktopSlotStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD { Modifier.fillMaxWidth().display(DisplayStyle.Flex).gap(0.8.cssRem).padding(top = 0.35.cssRem, bottom = 0.35.cssRem) }
}

val TimeStyle = CssStyle {
    base { Modifier.fontSize(0.88.cssRem).fontWeight(FontWeight.Bold).padding(leftRight = 0.15.cssRem).color(SitePalette.brand.ink) }
    Breakpoint.MD { Modifier.display(DisplayStyle.Flex).width(3.6.cssRem).fontSize(1.12.cssRem).padding(top = 0.25.cssRem).textAlign(com.varabyte.kobweb.compose.css.TextAlign.End) }
}

val EmptyTimeStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD { Modifier.display(DisplayStyle.Flex).width(3.6.cssRem) }
}

val CardsStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(0.8.cssRem).alignItems(AlignItems.Stretch).styleModifier { property("flex", "1 1 0") }
    }
}

val SessionCardStyle = CssStyle {
    base {
        Modifier
            .padding(0.85.cssRem)
            .border(1.px, LineStyle.Solid, SitePalette.brand.border)
            .borderRadius(0.7.cssRem)
            .backgroundColor(SitePalette.brand.white)
            .gap(0.32.cssRem)
            .styleModifier { property("box-sizing", "border-box") }
    }
}

val SessionTimeStyle = CssStyle.base {
    Modifier.fontSize(0.56.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.brand.ink)
}

val SessionTitleStyle = CssStyle.base {
    Modifier.fontSize(0.84.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.55).color(SitePalette.brand.ink)
}

val TrackTitleStyle = CssStyle.base {
    Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.45).color(timetablePink)
}

val SpeakerStyle = CssStyle.base {
    Modifier.fontSize(0.62.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.brand.ink)
}

val TagStyle = CssStyle.base {
    Modifier.padding(topBottom = 0.2.cssRem, leftRight = 0.42.cssRem).borderRadius(0.35.cssRem).backgroundColor(timetableLime)
        .fontSize(0.47.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.brand.ink)
}

val MobileVenueStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 0.16.cssRem, leftRight = 0.4.cssRem)
            .borderRadius(0.3.cssRem)
            .fontSize(0.5.cssRem)
            .fontWeight(FontWeight.Bold)
            .color(SitePalette.brand.ink)
    }
    Breakpoint.MD { Modifier.display(DisplayStyle.None) }
}

val TimetableActionsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.Center)
            .padding(bottom = 1.25.cssRem)
    }
    Breakpoint.MD { Modifier.justifyContent(JustifyContent.FlexEnd) }
}

val MyTimetableLinkStyle = CssStyle.base {
    Modifier
        .padding(topBottom = 0.7.cssRem, leftRight = 1.25.cssRem)
        .borderRadius(2.cssRem)
        .backgroundColor(timetableLime)
        .fontSize(0.78.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(SitePalette.brand.ink)
}

val TimetableToggleStyle = CssStyle.base {
    Modifier
        .padding(topBottom = 0.38.cssRem, leftRight = 0.65.cssRem)
        .border(1.px, LineStyle.Solid, timetablePink)
        .borderRadius(2.cssRem)
        .backgroundColor(SitePalette.brand.white)
        .fontSize(0.56.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(SitePalette.brand.ink)
        .styleModifier { property("cursor", "pointer") }
}

val MyTimetableIntroStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(0.9.cssRem)
        .padding(bottom = 1.5.cssRem)
        .fontSize(0.78.cssRem)
        .lineHeight(1.7)
        .color(SitePalette.brand.ink)
}

val EmptyMyTimetableStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(2.cssRem)
        .border(1.px, LineStyle.Solid, SitePalette.brand.border)
        .borderRadius(0.7.cssRem)
        .backgroundColor(SitePalette.nearBackground)
        .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
        .fontSize(0.78.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(SitePalette.brand.muted)
}

val ShareActionsStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .gap(0.6.cssRem)
        .styleModifier { property("flex-wrap", "wrap") }
}

val ShareQrStyle = CssStyle.base {
    Modifier
        .width(12.cssRem)
        .height(12.cssRem)
        .padding(0.75.cssRem)
        .border(1.px, LineStyle.Solid, SitePalette.brand.border)
        .borderRadius(0.7.cssRem)
        .backgroundColor(SitePalette.brand.white)
}

val NotificationNoticeStyle = CssStyle.base {
    Modifier
        .fontSize(0.66.cssRem)
        .lineHeight(1.6)
        .color(SitePalette.brand.muted)
}

val GuideOverlayStyle = CssStyle.base {
    Modifier
        .position(Position.Fixed)
        .top(0.px)
        .right(0.px)
        .bottom(0.px)
        .left(0.px)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .padding(1.cssRem)
        .backgroundColor(Color.rgba(0, 0, 0, 0.55f))
        .styleModifier { property("z-index", "1000") }
}

val GuideDialogStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(34.cssRem)
            .padding(1.5.cssRem)
            .gap(1.25.cssRem)
            .borderRadius(1.cssRem)
            .backgroundColor(SitePalette.brand.white)
            .color(SitePalette.brand.ink)
            .styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD { Modifier.padding(2.cssRem) }
}

val GuideTitleStyle = CssStyle.base {
    Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.5)
}

val GuideStepStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(0.9.cssRem)
        .gap(0.65.cssRem)
        .borderRadius(0.7.cssRem)
        .backgroundColor(SitePalette.nearBackground)
        .styleModifier { property("box-sizing", "border-box") }
}

val GuideStepNumberStyle = CssStyle.base {
    Modifier
        .width(1.7.cssRem)
        .height(1.7.cssRem)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .borderRadius(50.percent)
        .backgroundColor(timetablePink)
        .fontSize(0.72.cssRem)
        .fontWeight(FontWeight.Bold)
        .styleModifier { property("flex", "0 0 auto") }
}

val GuideTextStyle = CssStyle.base {
    Modifier.fontSize(0.72.cssRem).lineHeight(1.65)
}

private data class Session(
    val time: String,
    val title: String,
    val speaker: String? = null,
    val affiliation: String? = null,
    val track: Boolean = false,
    val tags: List<String> = emptyList(),
    val venueColumns: Int? = null,
    val venue: String? = null,
    val id: String? = null,
    val emptySlot: Boolean = false,
)

private fun Session.desktopFlex() = when (venueColumns) {
    1 -> "0 1 calc(33.333333% - 0.533333rem)"
    2 -> "0 1 calc(66.666667% - 0.266667rem)"
    else -> "1 1 0"
}

@Composable
private fun SessionCard(
    session: Session,
    isDesktop: Boolean = false,
    selected: Boolean = false,
    onToggle: ((String) -> Unit)? = null,
) {
    val cardModifier = SessionCardStyle.toModifier()
        .border(
            1.px,
            if (session.emptySlot) LineStyle.Dashed else LineStyle.Solid,
            if (session.track) timetablePink else SitePalette.brand.border,
        )
        .then(if (session.emptySlot) Modifier.backgroundColor(SitePalette.nearBackground) else Modifier)
        .let {
            if (isDesktop) {
                it.fillMaxHeight().styleModifier {
                    property("flex", session.desktopFlex())
                    property("min-width", "0")
                }
            } else {
                it.fillMaxWidth()
            }
        }
    val cardContent: @Composable () -> Unit = {
        Column(cardModifier) {
            Row(Modifier.fillMaxWidth().justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.Center).gap(0.5.cssRem)) {
                SpanText(session.time, SessionTimeStyle.toModifier())
                if (session.track && session.id != null && onToggle != null) {
                    Button(
                        onClick = { onToggle(session.id) },
                        modifier = TimetableToggleStyle.toModifier()
                            .backgroundColor(if (selected) timetablePink else SitePalette.brand.white)
                            .ariaLabel(if (selected) "${session.title}をマイタイムテーブルから削除" else "${session.title}をマイタイムテーブルに追加"),
                    ) {
                        SpanText(if (selected) "✓ 保存済み" else "＋ 保存")
                    }
                }
            }
            if (session.venue != null || session.tags.isNotEmpty()) {
                Row(Modifier.gap(0.25.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                    session.venue?.let { venue ->
                        SpanText(venue, MobileVenueStyle.toModifier().backgroundColor(sessionVenueColor(venue)))
                    }
                    session.tags.forEach { SpanText(it, TagStyle.toModifier()) }
                }
            }
            val details: @Composable () -> Unit = {
                Column(Modifier.gap(0.32.cssRem)) {
                    SpanText(
                        session.title,
                        when {
                            session.track -> TrackTitleStyle.toModifier()
                            session.emptySlot -> SessionTitleStyle.toModifier().color(SitePalette.brand.muted)
                            else -> SessionTitleStyle.toModifier()
                        },
                    )
                    session.affiliation?.let { SpanText(it, SpeakerStyle.toModifier()) }
                    session.speaker?.let { SpanText(it, SpeakerStyle.toModifier()) }
                }
            }
            if (session.track && session.id != null) {
                Link("/sessions/${session.id}", modifier = Modifier.fillMaxWidth(), variant = _sessionLinkVariant) { details() }
            } else {
                details()
            }
        }
    }
    cardContent()
}

@Composable
private fun TimetableSlot(
    time: String,
    sessions: List<Session>,
    selectedIds: Set<String>,
    onToggle: ((String) -> Unit)?,
) {
    Column(MobileSlotStyle.toModifier()) {
        if (time.isNotBlank()) SpanText(time, TimeStyle.toModifier())
        sessions.forEach { SessionCard(it, selected = it.id in selectedIds, onToggle = onToggle) }
    }
    Row(DesktopSlotStyle.toModifier()) {
        SpanText(time, if (time.isBlank()) EmptyTimeStyle.toModifier() else TimeStyle.toModifier())
        Row(CardsStyle.toModifier()) {
            sessions.forEach { SessionCard(it, isDesktop = true, selected = it.id in selectedIds, onToggle = onToggle) }
        }
    }
}

private fun UserSession.toTimetableSession() = Session(
    id = id,
    time = time,
    title = title,
    speaker = speaker,
    affiliation = affiliation,
    track = true,
    tags = listOfNotNull(category, level),
    venue = venue,
)

private fun userSession(id: String) = UserSessions.find(id)!!.toTimetableSession()

@InitRoute
fun initTimetablePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Timetable", fullWidth = true))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun TimetablePage() {
    var selectedIds by remember { mutableStateOf(MyTimetable.read()) }
    var showGuide by remember { mutableStateOf(false) }
    val isAutomatedBrowser = window.navigator.asDynamic().webdriver == true
    if (!isAutomatedBrowser) {
        LaunchedEffect(Unit) {
            showGuide = !MyTimetable.hasSeenGuide()
        }
    }
    val toggleSession: (String) -> Unit = { id ->
        val session = UserSessions.find(id)
        selectedIds = if (id in selectedIds) {
            selectedIds - id
        } else {
            selectedIds.filterTo(mutableSetOf()) { selectedId -> UserSessions.find(selectedId)?.time != session?.time } + id
        }
        MyTimetable.write(selectedIds)
    }
    Column(Modifier.fillMaxWidth()) {
        Column(TimetableHeroStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            SpanText("Timetable", TimetableHeroTitleStyle.toModifier())
            SpanText("タイムテーブル", TimetableHeroSubStyle.toModifier())
        }
        Column(TimetableContentStyle.toModifier()) {
            Row(TimetableActionsStyle.toModifier()) {
                Row(Modifier.gap(0.6.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                    Button(
                        onClick = { showGuide = true },
                        modifier = MyTimetableLinkStyle.toModifier().border(1.px, LineStyle.Solid, timetablePink)
                            .backgroundColor(SitePalette.brand.white)
                            .styleModifier { property("cursor", "pointer") },
                    ) {
                        SpanText("使い方")
                    }
                    Link("/timetable/me", modifier = MyTimetableLinkStyle.toModifier(), variant = _sessionLinkVariant) {
                        SpanText("マイタイムテーブルを見る")
                    }
                }
            }
            Row(VenueRowStyle.toModifier()) {
                SpanText("fun - ホールA", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("fun - ホールA")))
                SpanText("val - ホールB", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("val - ホールB")))
                SpanText("?.let - 402N", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("?.let - 402N")))
            }
            TimetableSchedule(selectedIds, toggleSession)
        }
        if (showGuide) {
            MyTimetableGuide {
                MyTimetable.markGuideAsSeen()
                showGuide = false
            }
        }
    }
}

@Composable
private fun MyTimetableGuide(onClose: () -> Unit) {
    Column(
        GuideOverlayStyle.toModifier()
            .role("dialog")
            .attr("aria-modal", "true")
            .ariaLabel("マイタイムテーブルの使い方"),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(GuideDialogStyle.toModifier()) {
            Column(Modifier.gap(0.25.cssRem)) {
                SpanText("マイタイムテーブルの使い方", GuideTitleStyle.toModifier())
                SpanText("気になるセッションを選んで、自分だけの当日の予定を作れます。", GuideTextStyle.toModifier())
            }
            GuideStep("1", "セッションを保存", "タイムテーブルの「＋ 保存」を押します。同じ時間帯では1件だけ選べます。")
            GuideStep("2", "空いている時間を埋める", "マイタイムテーブルでは、まだ選んでいない時間帯の候補からそのまま追加できます。")
            GuideStep("3", "予定を共有", "完成したタイムテーブルは共有ページ、QRコード、Xから共有できます。")
            Button(
                onClick = { onClose() },
                modifier = MyTimetableLinkStyle.toModifier().fillMaxWidth().border(0.px).styleModifier {
                    property("cursor", "pointer")
                    property("justify-content", "center")
                },
            ) {
                SpanText("マイタイムテーブルを作る")
            }
        }
    }
}

@Composable
private fun GuideStep(number: String, title: String, description: String) {
    Row(GuideStepStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
        SpanText(number, GuideStepNumberStyle.toModifier())
        Column(Modifier.gap(0.15.cssRem)) {
            SpanText(title, GuideTextStyle.toModifier().fontWeight(FontWeight.Bold))
            SpanText(description, GuideTextStyle.toModifier())
        }
    }
}

@Composable
internal fun TimetableSchedule(
    selectedIds: Set<String> = emptySet(),
    onToggle: ((String) -> Unit)? = null,
    selectedOnly: Boolean = false,
    showCandidatesForEmptySlots: Boolean = false,
    showEmptySlots: Boolean = false,
) {
    fun sessions(vararg sessions: Session): List<Session> {
        if (!selectedOnly) return sessions.toList()
        if (sessions.all { !it.track }) return sessions.toList()

        val selectedSessions = sessions.filter { it.id in selectedIds }
        return when {
            selectedSessions.isNotEmpty() -> selectedSessions
            showCandidatesForEmptySlots -> sessions.toList()
            else -> emptyList()
        }
    }

    @Composable
    fun slot(time: String, vararg slotSessions: Session) {
        val visibleSessions = sessions(*slotSessions)
        when {
            visibleSessions.isNotEmpty() -> TimetableSlot(time, visibleSessions, selectedIds, onToggle)
            showEmptySlots -> TimetableSlot(
                time,
                listOf(Session(slotSessions.first().time, "セッションが選択されていません", emptySlot = true)),
                selectedIds,
                onToggle,
            )
        }
    }

    slot("10:00", Session("10:00 ～ 11:00", "開場・受付", venue = "全会場"))
    slot(
        "11:00",
        Session("11:00 ～ 11:20", "オープニングセッション", venueColumns = 2, venue = "fun - ホールA / val - ホールB"),
    )
    slot("11:20", Session("11:20 ～ 11:30", "休憩", venue = "全会場"))
    slot(
        "11:30",
        userSession("invited-talk").copy(venueColumns = 2),
    )
    slot("12:00", Session("12:00 ～ 13:20", "昼休憩", venue = "全会場"))
    slot("13:20", userSession("session-01"), userSession("session-02"), userSession("session-03"))
    slot("14:00", Session("14:00 ～ 14:15", "休憩", venue = "全会場"))
    slot("14:15", userSession("session-04"), userSession("session-05"), userSession("session-06"))
    slot("14:35", Session("14:35 ～ 14:50", "休憩", venue = "全会場"))
    slot("14:50", userSession("session-07"), userSession("session-08"), userSession("session-09"))
    slot("15:30", Session("15:30 ～ 15:55", "休憩（ロング）", venue = "全会場"))
    slot("15:55", userSession("session-10"), userSession("session-11"), userSession("session-12"))
    slot("16:15", Session("16:15 ～ 16:30", "休憩", venue = "全会場"))
    slot("16:30", userSession("session-13"), userSession("session-14"), userSession("session-15"))
    slot("17:10", Session("17:10 ～ 17:25", "休憩", venue = "全会場"))
    slot("17:25", userSession("session-16"), userSession("session-17"), userSession("session-18"))
    slot("17:45", Session("17:45 ～ 18:00", "休憩", venue = "全会場"))
    slot("18:00", userSession("session-19"), userSession("session-20"), userSession("session-21"))
    slot("18:40", Session("18:40 ～ 18:50", "休憩", venue = "全会場"))
    slot(
        "18:50",
        Session("18:50 ～ 19:00", "クロージングセッション", venueColumns = 1, venue = "fun - ホールA"),
    )
}

@Page("/timetable/me")
@Layout(".components.layouts.PageLayout")
@Composable
fun MyTimetablePage() {
    var selectedIds by remember { mutableStateOf(MyTimetable.read()) }
    var qrCodeUrl by remember { mutableStateOf<String?>(null) }
    var notificationsEnabled by remember { mutableStateOf(MyTimetable.notificationsEnabled()) }
    var notificationMessage by remember { mutableStateOf<String?>(null) }
    val toggleSession: (String) -> Unit = { id ->
        val session = UserSessions.find(id)
        selectedIds = if (id in selectedIds) {
            selectedIds - id
        } else {
            selectedIds.filterTo(mutableSetOf()) { selectedId -> UserSessions.find(selectedId)?.time != session?.time } + id
        }
        MyTimetable.write(selectedIds)
    }
    val shareQuery = MyTimetable.encode(selectedIds)
    val shareUrl = "${window.location.origin}/timetable/your?m=$shareQuery"
    val xShareText = "Kotlin Fest 2026のマイタイムテーブルを作成しました！\n\n$shareUrl\n\n#KotlinFest"
    val xShareUrl = "https://x.com/intent/tweet?text=${encodeURIComponent(xShareText)}"
    LaunchedEffect(shareUrl) {
        if (qrCodeUrl != null) {
            QrCode.toDataURL(shareUrl).then { qrCodeUrl = it }
        }
    }
    TimetableView(title = "My Timetable", subtitle = "マイタイムテーブル") {
        Column(MyTimetableIntroStyle.toModifier()) {
            SpanText("選択済みのセッションと、まだ選んでいない時間帯の候補を表示しています。選択内容はこのブラウザに保存されます。")
            Row(Modifier.gap(0.6.cssRem).styleModifier { property("flex-wrap", "wrap") }) {
                Link("/timetable", modifier = MyTimetableLinkStyle.toModifier(), variant = _sessionLinkVariant) {
                    SpanText("タイムテーブルに戻る")
                }
                if (selectedIds.isNotEmpty()) {
                    Link(
                        "/timetable/your?m=$shareQuery",
                        modifier = MyTimetableLinkStyle.toModifier().backgroundColor(timetablePink),
                        variant = _sessionLinkVariant,
                    ) {
                        SpanText("共有用ページを表示")
                    }
                }
            }
            if (selectedIds.isNotEmpty()) {
                Row(ShareActionsStyle.toModifier()) {
                    Button(
                        onClick = {
                            if (qrCodeUrl == null) {
                                QrCode.toDataURL(shareUrl).then { qrCodeUrl = it }
                            } else {
                                qrCodeUrl = null
                            }
                        },
                        modifier = MyTimetableLinkStyle.toModifier().border(0.px).styleModifier { property("cursor", "pointer") },
                    ) {
                        SpanText(if (qrCodeUrl == null) "QRコードを表示" else "QRコードを閉じる")
                    }
                    Link(
                        xShareUrl,
                        modifier = MyTimetableLinkStyle.toModifier().backgroundColor(SitePalette.brand.ink).color(SitePalette.brand.white)
                            .attr("target", "_blank")
                            .attr("rel", "noopener noreferrer"),
                        variant = _sessionLinkVariant,
                    ) {
                        SpanText("Xで共有")
                    }
                    Button(
                        onClick = {
                            if (notificationsEnabled) {
                                notificationsEnabled = false
                                MyTimetable.setNotificationsEnabled(false)
                                notificationMessage = "セッション通知をOFFにしました。"
                            } else if (window.asDynamic().Notification == null) {
                                notificationMessage = "このブラウザは通知に対応していません。"
                            } else {
                                BrowserNotification.requestPermission().then { permission ->
                                    if (permission == "granted") {
                                        notificationsEnabled = true
                                        MyTimetable.setNotificationsEnabled(true)
                                        notificationMessage = "セッション開始5分前の通知をONにしました。"
                                    } else {
                                        notificationMessage = "通知が許可されませんでした。ブラウザの設定をご確認ください。"
                                    }
                                }
                            }
                        },
                        modifier = MyTimetableLinkStyle.toModifier()
                            .border(1.px, LineStyle.Solid, timetableBlue)
                            .backgroundColor(if (notificationsEnabled) timetableBlue else SitePalette.brand.white)
                            .styleModifier { property("cursor", "pointer") },
                    ) {
                        SpanText(if (notificationsEnabled) "通知ON" else "開始5分前に通知")
                    }
                }
                qrCodeUrl?.let { Image(it, "共有用タイムテーブルのQRコード", ShareQrStyle.toModifier()) }
                SpanText(
                    notificationMessage ?: "通知はKotlin Festサイトを開いている間だけ届きます。",
                    NotificationNoticeStyle.toModifier(),
                )
            }
        }
        VenueRow()
        TimetableSchedule(
            selectedIds = selectedIds,
            onToggle = toggleSession,
            selectedOnly = true,
            showCandidatesForEmptySlots = true,
        )
    }
}

@Page("/timetable/your")
@Layout(".components.layouts.PageLayout")
@Composable
fun SharedTimetablePage() {
    var showCopyConfirmation by remember { mutableStateOf(false) }
    val sharedIds = remember {
        val value = window.location.search
            .removePrefix("?")
            .split('&')
            .firstOrNull { it.substringBefore('=') == "m" }
            ?.substringAfter('=', "")
        MyTimetable.decode(value)
    }
    val copyAndEdit: () -> Unit = {
        MyTimetable.write(sharedIds)
        window.location.href = "/timetable/me"
    }

    TimetableView(title = "Shared Timetable", subtitle = "共有タイムテーブル") {
        Column(MyTimetableIntroStyle.toModifier()) {
            SpanText("共有されたタイムテーブルです。")
            if (sharedIds.isNotEmpty()) {
                Button(
                    onClick = {
                        val currentIds = MyTimetable.read()
                        if (currentIds.isNotEmpty() && currentIds != sharedIds) {
                            showCopyConfirmation = true
                        } else {
                            copyAndEdit()
                        }
                    },
                    modifier = MyTimetableLinkStyle.toModifier()
                        .border(0.px)
                        .backgroundColor(timetablePink)
                        .styleModifier { property("cursor", "pointer") },
                ) {
                    SpanText("このタイムテーブルをコピーして編集")
                }
            } else {
                Link("/timetable", modifier = MyTimetableLinkStyle.toModifier(), variant = _sessionLinkVariant) {
                    SpanText("自分のタイムテーブルを作る")
                }
            }
        }
        if (sharedIds.isEmpty()) {
            SpanText("共有されたセッションがありません。", EmptyMyTimetableStyle.toModifier())
        } else {
            VenueRow()
            TimetableSchedule(sharedIds, selectedOnly = true, showEmptySlots = true)
        }
    }
    if (showCopyConfirmation) {
        CopyTimetableConfirmation(
            onCancel = { showCopyConfirmation = false },
            onCopy = copyAndEdit,
        )
    }
}

@Composable
private fun CopyTimetableConfirmation(
    onCancel: () -> Unit,
    onCopy: () -> Unit,
) {
    Column(
        GuideOverlayStyle.toModifier()
            .role("dialog")
            .attr("aria-modal", "true")
            .ariaLabel("タイムテーブルの上書き確認"),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(GuideDialogStyle.toModifier()) {
            Column(Modifier.gap(0.45.cssRem)) {
                SpanText("現在のマイタイムテーブルを置き換えますか？", GuideTitleStyle.toModifier())
                SpanText(
                    "保存済みのセッションは、共有されたタイムテーブルの内容に置き換わります。",
                    GuideTextStyle.toModifier(),
                )
            }
            Row(ShareActionsStyle.toModifier().justifyContent(JustifyContent.FlexEnd)) {
                Button(
                    onClick = { onCancel() },
                    modifier = MyTimetableLinkStyle.toModifier()
                        .border(1.px, LineStyle.Solid, SitePalette.brand.border)
                        .backgroundColor(SitePalette.brand.white)
                        .styleModifier { property("cursor", "pointer") },
                ) {
                    SpanText("キャンセル")
                }
                Button(
                    onClick = { onCopy() },
                    modifier = MyTimetableLinkStyle.toModifier()
                        .border(0.px)
                        .backgroundColor(timetablePink)
                        .styleModifier { property("cursor", "pointer") },
                ) {
                    SpanText("コピーして編集")
                }
            }
        }
    }
}

@Composable
private fun TimetableView(
    title: String,
    subtitle: String,
    content: @Composable () -> Unit,
) {
    Column(Modifier.fillMaxWidth()) {
        Column(TimetableHeroStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            SpanText(title, TimetableHeroTitleStyle.toModifier())
            SpanText(subtitle, TimetableHeroSubStyle.toModifier())
        }
        Column(TimetableContentStyle.toModifier()) { content() }
    }
}

@Composable
private fun VenueRow() {
    Row(VenueRowStyle.toModifier()) {
        SpanText("fun - ホールA", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("fun - ホールA")))
        SpanText("val - ホールB", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("val - ホールB")))
        SpanText("?.let - 402N", VenueLabelStyle.toModifier().backgroundColor(sessionVenueColor("?.let - 402N")))
    }
}
